
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Layout } from '@/components/layout';
import { ProtectedRoute } from '@/components/auth';
import { Dashboard } from '@/components/dashboard';
import { Login } from '@/components/auth';
import { RegisterPage } from '@/components/auth/register/pages/RegisterPage';
import { AppWrapper } from '@/components/common/AppWrapper';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useDefaultAdmin } from '@/hooks/useDefaultAdmin';
import { Button } from '@/components/ui/button';

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, initializeAuth } = useAuthStore();
  const { isLoading: isSettingUpAdmin, error: adminError } = useDefaultAdmin();

  useEffect(() => {
    async function init() {
      try {
        setIsLoading(true);
        setError(null);
        await initializeAuth();
      } catch (err) {
        setError('حدث خطأ أثناء تحميل التطبيق');
        console.error('App initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    }

    init();
  }, []);

  if (isLoading || isSettingUpAdmin) {
    return <LoadingScreen />;
  }

  if (error || adminError) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {error || adminError}
          </h1>
          <Button onClick={() => window.location.reload()}>
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AppWrapper>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/register" element={<RegisterPage />} />

            {/* Protected admin routes */}
            <Route
              path="/dashboard/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Login route */}
            <Route
              path="/login"
              element={
                user ? <Navigate to="/dashboard/tree" replace /> : <Login />
              }
            />

            {/* Home route */}
            <Route
              path="/"
              element={
                user ? (
                  <Navigate to="/dashboard/tree" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AppWrapper>
    </ErrorBoundary>
  );
}
